import { useEffect, useRef } from 'react';
import axios from 'axios';
import API from '../utils/api';

/**
 * Polls /api/video/rooms every 15s for incoming video-call invites and drives
 * the global IncomingCallOverlay (mounted in Dashboard) via window events:
 *   - `incoming-call-show`  → detail = room
 *   - `incoming-call-hide`  → no detail (caller cancelled, expired, accepted elsewhere)
 *
 * Also fires a native browser Notification when permission is granted, so
 * users get pinged even on a backgrounded tab. The browser notification
 * click brings the tab to front and triggers the same Accept handoff.
 */
export function useIncomingCallNotifier(userId) {
  // room_sid -> { browserNotif }
  var activeRef = useRef({});
  // The single room we're currently showing in the overlay (only one at a time).
  var currentSidRef = useRef(null);

  useEffect(function () {
    if (!userId) return undefined;
    var cancelled = false;

    function clearOverlayIfMatches(roomSid) {
      if (currentSidRef.current === roomSid) {
        currentSidRef.current = null;
        window.dispatchEvent(new CustomEvent('incoming-call-hide'));
      }
      var entry = activeRef.current[roomSid];
      if (entry && entry.browserNotif) {
        try { entry.browserNotif.close(); } catch (e) { /* ignore */ }
      }
      delete activeRef.current[roomSid];
    }

    function showInvite(room) {
      // If we're already showing this exact room, don't re-fire.
      if (activeRef.current[room.room_sid]) return;

      // Drive the overlay (only one at a time — first invite wins).
      if (!currentSidRef.current) {
        currentSidRef.current = room.room_sid;
        window.dispatchEvent(new CustomEvent('incoming-call-show', { detail: room }));
      }

      // Browser-level notification when permission was granted.
      var browserNotif = null;
      try {
        if ('Notification' in window && Notification.permission === 'granted') {
          var caller = room.initiator_name || room.partner_name || 'Co-parent';
          browserNotif = new Notification('Incoming video call from ' + caller, {
            body: (room.recording_enabled ? 'Recording will be ON' : 'Recording will be OFF') + ' · Open Parentum to join.',
            tag: 'parentum-call-' + room.room_sid,
            requireInteraction: true,
          });
          browserNotif.onclick = function () {
            window.focus();
            // Re-fire show so the overlay opens if it isn't already.
            if (!currentSidRef.current) {
              currentSidRef.current = room.room_sid;
              window.dispatchEvent(new CustomEvent('incoming-call-show', { detail: room }));
            }
            try { browserNotif.close(); } catch (e) { /* ignore */ }
          };
        }
      } catch (e) { /* ignore */ }

      activeRef.current[room.room_sid] = { browserNotif: browserNotif };
    }

    var fetchOnce = async function () {
      try {
        var r = await axios.get(API + '/video/rooms', { withCredentials: true });
        if (cancelled) return;
        var items = (r.data && r.data.items) || [];
        var incoming = items.filter(function (room) {
          return room.status === 'pending' && room.invited_user_id === userId;
        });
        var liveSids = new Set(incoming.map(function (room) { return room.room_sid; }));

        // Clear anything no longer pending.
        Object.keys(activeRef.current).forEach(function (sid) {
          if (!liveSids.has(sid)) clearOverlayIfMatches(sid);
        });

        // Show fresh invites.
        incoming.forEach(showInvite);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('incoming-calls poll failed', e);
      }
    };

    fetchOnce();
    var id = setInterval(fetchOnce, 15000);

    var onFocus = function () { fetchOnce(); };
    window.addEventListener('focus', onFocus);

    var onRefresh = function () { fetchOnce(); };
    window.addEventListener('incoming-calls-refresh', onRefresh);

    return function () {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('incoming-calls-refresh', onRefresh);
      Object.keys(activeRef.current).forEach(clearOverlayIfMatches);
    };
  }, [userId]);
}
